import { and, eq, isNotNull } from "@-/com.npmjs.drizzle-orm";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { getQueryDatabaseId } from "#get-query-database-id";

function useGetCommonMorphemeCompoundCharactersQueryKey() {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  return (graphemeId: number, hanziScript: "simplified" | "traditional") =>
    [
      "commonMorphemeCompoundCharacters",
      graphemeId,
      hanziScript,
      getQueryDatabaseId(contentDb),
      getQueryDatabaseId(staticDb),
      { contentDb, staticDb },
    ] as const;
}

/**
 * The complete display order, without hydrating descriptions, pronunciations or per-card queries.
 * PRC tier 1 / Taiwan TBCL are the same common-character bands used by grapheme prevalence.
 * Only mounted sheet rows resolve their authored detail slice.
 */
export const [
  useCommonMorphemeCompoundCharactersQueryOptions,
  commonMorphemeCompoundCharactersQueryFn,
] = createUseQueryOptions(
  useGetCommonMorphemeCompoundCharactersQueryKey,
  async ({
    queryKey: {
      1: graphemeId,
      2: hanziScript,
      5: { contentDb, staticDb },
    },
  }) => {
    const db = staticDb(import.meta.location);
    const rows = await db
      .selectDistinct({
        biauRank: staticSchema.graphemeBiau.rank,
        character: staticSchema.grapheme.glyph,
        subtlexRank: staticSchema.graphemeSubtlex.rank,
      })
      .from(staticSchema.graphemeComponent)
      .innerJoin(
        staticSchema.grapheme,
        eq(staticSchema.grapheme.id, staticSchema.graphemeComponent.graphemeId),
      )
      .leftJoin(
        staticSchema.graphemePrcData,
        eq(staticSchema.graphemePrcData.graphemeId, staticSchema.grapheme.id),
      )
      .leftJoin(
        staticSchema.graphemeTwData,
        eq(staticSchema.graphemeTwData.graphemeId, staticSchema.grapheme.id),
      )
      .leftJoin(
        staticSchema.graphemeBiau,
        eq(staticSchema.graphemeBiau.graphemeId, staticSchema.grapheme.id),
      )
      .leftJoin(
        staticSchema.graphemeSubtlex,
        eq(staticSchema.graphemeSubtlex.graphemeId, staticSchema.grapheme.id),
      )
      .where(
        and(
          eq(staticSchema.graphemeComponent.componentGraphemeId, graphemeId),
          hanziScript === "traditional"
            ? isNotNull(staticSchema.graphemeTwData.tbclLevel)
            : eq(staticSchema.graphemePrcData.tier, 1),
        ),
      );

    const ranked: { character: string; rank: number }[] = [];
    // Keep each remote D1 IN-list below its parameter limit. These projected transient rows never
    // enter the QueryCache; the one query retains only the final ordered character strings.
    for (let offset = 0; offset < rows.length; offset += 64) {
      const page = rows.slice(offset, offset + 64);
      const authored = await contentDb(import.meta.location).query.appGraphemeMeaningCard.findMany({
        columns: { grapheme: true },
        where: { grapheme: { in: page.map((row) => row.character) } },
      });
      const authoredCharacters = new Set(authored.map((row) => row.grapheme));
      const missing = page.filter((row) => !authoredCharacters.has(row.character));
      // The existing detail resolver falls back to a word for a missing authored character.
      // Its word fallback has only SUBTLEX rank; traditional therefore still sorts it last.
      const words =
        hanziScript === "traditional" || missing.length === 0
          ? []
          : await db.query.word.findMany({
              columns: { word: true },
              where: { word: { in: missing.map((row) => row.character) } },
              with: { subtlex: { columns: { rank: true } } },
            });
      const fallbackRank = new Map(words.map((word) => [word.word, word.subtlex?.rank]));
      for (const row of page) {
        const rank = authoredCharacters.has(row.character)
          ? hanziScript === "traditional"
            ? row.biauRank
            : row.subtlexRank
          : fallbackRank.get(row.character);
        ranked.push({ character: row.character, rank: rank ?? Number.POSITIVE_INFINITY });
      }
    }
    ranked.sort((a, b) => a.rank - b.rank || a.character.localeCompare(b.character));
    return ranked.map((row) => row.character);
  },
);
